import { Check, InfoCircle, XClose } from '@timelyapp/icons/solid-icons';
import type { ConnectionSettings, Providers } from '@timelyapp/integration-cloud-api-spec';
import { Box, type BoxProps, Pill, Text, RadixTooltip as Tooltip } from '@timelyapp/react';
import React from 'react';
import { ticApi } from '~redux/modules/tic/api-slice/tic.apiSlice';
import type { UseIntegrationModalManagerWithDetails } from '../../hooks/useIntegrationModalManager';

import {
  buildFormattedSyncSettings,
  buildSettingTooltip,
  type FormattedSyncSetting,
} from '../../utils/util.syncSettingsDisplay';

export interface IntegrationsWidgetsSyncSettingsProps extends Omit<BoxProps, 'children'> {
  configManager: UseIntegrationModalManagerWithDetails;
  overrides?: {
    settings?: Partial<ConnectionSettings>;
    imported_data_access?: 'default' | 'everyone';
  };
  children?: React.ReactNode;
  isTimelyEmployee?: boolean;
}

/**
 * Displays sync settings derived from operations format with embedded settings.
 */
export const IntegrationsWidgetsSyncSettings: React.FC<IntegrationsWidgetsSyncSettingsProps> = ({
  configManager,
  overrides,
  isTimelyEmployee = false,
  ...rest
}) => {
  const { active, integrationDetails } = configManager;

  const settings = React.useMemo(
    () => ({ ...active.connection?.settings, ...overrides?.settings }),
    [active.connection?.settings, overrides?.settings]
  );
  const importedDataAccess =
    overrides?.imported_data_access ?? active.connection?.imported_data_access ?? 'everyone';
  const integrationName = integrationDetails.title || 'integration';
  const providerTitle = configManager.providerTitle || integrationDetails.title || active.toolId;
  const activeProfile = active.activeProfile;

  const processedSettings = configManager.active.processedSyncSettings;

  const formattedSettings = React.useMemo(
    () =>
      buildFormattedSyncSettings({
        processedSettings,
        activeProfile,
        providerTitle,
        overrides: overrides?.settings,
        isTimelyEmployee,
      }),
    [processedSettings, activeProfile, providerTitle, overrides?.settings, isTimelyEmployee]
  );

  const { resourceCountText } = useSubscriptionResourceText({
    accountId: active.workspaceAccountId,
    connectionId: active.connection?.id!,
    provider: active.toolId,
  });

  // Helper to render sync bullet based on status
  const renderSyncBullet = (status: 'enabled' | 'disabled') => {
    if (status === 'enabled') {
      return (
        <Pill
          variant="success"
          style={{
            width: 24,
            height: 24,
            padding: 0,
          }}
        >
          <Check width={16} height={16} />
        </Pill>
      );
    }
    return <XClose width={16} height={16} style={{ marginLeft: 4, marginRight: 4 }} />;
  };

  // Build enhanced tooltip with dependencies
  const renderSettingMetadata = (
    formatted: FormattedSyncSetting,
    includeDisabledDependencies = true
  ) => {
    const enhancedTooltip = buildSettingTooltip(formatted, formatted.dependencyTitles, {
      includeDisabledDependencies,
    });
    const displaySource = formatted.displaySource;

    if (!displaySource && !enhancedTooltip) return null;

    return (
      <Text size="small" color="subtle">
        {displaySource}
        {enhancedTooltip && (
          <>
            {' '}
            <Tooltip tooltip={enhancedTooltip}>
              <span>
                <InfoCircle width={14} height={14} color="var(--theme_color_content_subtle)" />
              </span>
            </Tooltip>
          </>
        )}
      </Text>
    );
  };

  // Render sync feature from processed settings
  const renderSyncItems = (formatted: FormattedSyncSetting) => {
    const status = formatted.enabled ? 'enabled' : 'disabled';

    return (
      <Box.Column key={formatted.key} gap="xsmall">
        <Box.Row alignItems="center" gap="medium">
          {renderSyncBullet(status)}
          <Text bold={status === 'enabled'} size={status === 'enabled' ? 'medium' : 'small'}>
            {formatted.label}
          </Text>
          {renderSettingMetadata(formatted)}
        </Box.Row>
      </Box.Column>
    );
  };

  // Render additional feature (resource count or sync mode)
  const renderAdditionalFeature = (
    key: string,
    text: string,
    enabled: boolean,
    tooltip?: string
  ) => (
    <Box.Row key={key} alignItems="flex-start" gap="medium" flexWrap="nowrap">
      {renderSyncBullet(enabled ? 'enabled' : 'disabled')}
      <Text color="subtle" size="small">
        {text}
      </Text>
      {!!tooltip && (
        <Tooltip tooltip={tooltip}>
          <span>
            <InfoCircle width={14} height={14} color="var(--theme_color_content_subtle)" />
          </span>
        </Tooltip>
      )}
    </Box.Row>
  );

  // Determine if webhooks are supported for the current scope
  const supportsWebhooks =
    active.scope === 'personal'
      ? integrationDetails.supports_personal_webhooks
      : integrationDetails.supports_account_webhooks;

  const continuousSyncEnabled = settings.sync_to_timely === 'continuous';
  const syncModeText = continuousSyncEnabled
    ? `${integrationName} changes will be synced to Timely in real-time`
    : `Updates on ${integrationName} will not be synced to Timely`;

  // Data access text
  const dataAccessRestricted = importedDataAccess === 'default';
  const dataAccessText = dataAccessRestricted
    ? `Data restricted to those who verify their identity on ${integrationName}`
    : `Relaxed data access, direct assignments not required.`;

  // Client fallback text
  const hasFallbackClient = Boolean(settings.fallback_timely_client_id);
  const fallbackClientText = hasFallbackClient
    ? `Fallback client configured for unmapped projects`
    : `No fallback client set`;

  // Separate enabled and disabled settings
  const enabledSettings = formattedSettings.filter(setting => setting.enabled);
  const disabledSettings = formattedSettings.filter(setting => !setting.enabled);

  const showImportProfileInfo = !!activeProfile && activeProfile?.key !== 'default';

  return (
    <Box.Column gap="medium" {...rest}>
      {/* Render enabled sync items */}
      {enabledSettings.map(renderSyncItems)}

      {/* If import profile is NOT `default`, show it */}
      {showImportProfileInfo &&
        renderAdditionalFeature(
          'import_profile',
          `Hierarchy: ${activeProfile?.title}`,
          true,
          activeProfile?.description || undefined
        )}

      {/* Resource count */}
      {resourceCountText && renderAdditionalFeature('resources', resourceCountText, true)}

      {/* Sync mode - only show if webhooks are supported for this scope */}
      {supportsWebhooks &&
        renderAdditionalFeature('sync_mode', syncModeText, continuousSyncEnabled)}

      {/* Data access */}
      {active.scope === 'account' &&
        renderAdditionalFeature('data_access', dataAccessText, !dataAccessRestricted)}

      {/* Client fallback */}
      {renderAdditionalFeature('client_fallback', fallbackClientText, hasFallbackClient)}

      {/* Render disabled sync items at the very bottom */}
      {disabledSettings.map(setting => {
        const enhancedTooltip = buildSettingTooltip(setting, setting.dependencyTitles);
        return (
          <Box.Row key={setting.key} alignItems="flex-start" gap="medium" flexWrap="nowrap">
            {renderSyncBullet('disabled')}
            <Text color="subtle" size="small">
              {setting.label}
              {setting.displaySource && <> {setting.displaySource}</>}
              {enhancedTooltip && (
                <>
                  {' '}
                  <Tooltip tooltip={enhancedTooltip}>
                    <span>
                      <InfoCircle
                        width={14}
                        height={14}
                        color="var(--theme_color_content_subtle)"
                      />
                    </span>
                  </Tooltip>
                </>
              )}
            </Text>
          </Box.Row>
        );
      })}
    </Box.Column>
  );
};

/**
 * Custom hook to handle subscription resource text calculation and loading
 */
const useSubscriptionResourceText = ({
  accountId,
  connectionId,
  provider,
}: {
  accountId: string;
  connectionId: string;
  provider: Providers;
}) => {
  // Load subscribable resources data for resource counts display
  const { data: subscribableResourcesData } = ticApi.useGetSubscribableResourcesQuery({
    accountId,
    connectionId,
    provider,
  });

  // Combined resource count with smart formatting
  const resourceCountText = subscribableResourcesData
    ? (() => {
        const allSubscriptions = subscribableResourcesData.subscriptions;
        const allSubSubscriptions = allSubscriptions.flatMap(sub => sub.sub_subscriptions || []);

        const parts: string[] = [];

        // Root resources
        if (allSubscriptions.length > 0) {
          const rootTypes = new Set(allSubscriptions.map(sub => sub.external_type || 'resource'));
          const rootEnabled = allSubscriptions.filter(sub => sub.enabled).length;
          const rootType = rootTypes.size === 1 ? Array.from(rootTypes)[0] : 'resources';

          if (rootEnabled === allSubscriptions.length) {
            parts.push(`Every ${rootType}`);
          } else if (rootEnabled === 0) {
            parts.push(`No ${rootType}`);
          } else {
            parts.push(`${rootEnabled}/${allSubscriptions.length} ${rootType}`);
          }
        }

        // Sub-resources
        if (allSubSubscriptions.length > 0) {
          const subTypes = new Set(allSubSubscriptions.map(sub => sub.external_type || 'resource'));
          const subEnabled = allSubSubscriptions.filter(sub => sub.enabled).length;
          const subType = subTypes.size === 1 ? Array.from(subTypes)[0] : 'resources';

          if (subEnabled === allSubSubscriptions.length) {
            parts.push(`every ${subType}`);
          } else if (subEnabled === 0) {
            parts.push(`no ${subType}`);
          } else {
            parts.push(`${subEnabled}/${allSubSubscriptions.length} ${subType}`);
          }
        }

        return parts.length > 0 ? parts.join(', ') + ' selected for synchronization' : null;
      })()
    : null;

  return {
    resourceCountText,
  };
};
